import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "banner-content" }
const _hoisted_2 = {
  class: "alert-text",
  "data-testid": "message"
}
const _hoisted_3 = ["aria-label"]

import { computed, onMounted, onBeforeUnmount } from 'vue';
import { BANNER_VARIANT } from '@/constants/AppConstants';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import { BannerStickyAlertPropsSchema } from '@/modules/alerts/models/banner-sticky-alert-props';

const BANNER_ANCHOR_SELECTOR = '#desktop-header-v2, .wc-mobile-subpage-header';
const STICKY_ALERT_ROOT_SELECTOR = '.sticky-alert-root';
const BANNER_TOP_VAR = '--wc-banner-top';

// A modal that owns its own alert area renders this host, and the banner moves inside it
// rather than floating over the page behind the modal. When no host is on the page the
// teleport is disabled and the banner keeps its usual fixed position under the header.
const INLINE_HOST_SELECTOR = '#wc-banner-host';

export default /*@__PURE__*/_defineComponent({
  __name: 'WCBannerStickyAlert',
  props: {
    variant: {},
    message: {},
    options: {},
    id: {}
  },
  setup(__props: any) {

const { findAlert, answerAlert, setupTimeout } = useStickyAlerts();

const props = __props;

const alert = findAlert(STICKY_ALERT_TYPE.BANNER, props.id);

const dismiss = () => {
  answerAlert(alert, { result: 'DISMISS' });
};

setupTimeout(STICKY_ALERT_TYPE.BANNER, props.id, alert, props.options?.timeoutMs);

const variantIcon = computed(() => {
  const map: Record<string, string[]> = {
    [BANNER_VARIANT.WARNING]: ['fas', 'triangle-exclamation'],
    [BANNER_VARIANT.DANGER]: ['fas', 'circle-exclamation'],
    [BANNER_VARIANT.SUCCESS]: ['fas', 'circle-check'],
    [BANNER_VARIANT.INFO]: ['fas', 'circle-info'],
  };
  return map[props.variant] ?? map[BANNER_VARIANT.WARNING];
});

const hasInlineHost = Boolean(document.querySelector(INLINE_HOST_SELECTOR));

const reposition = (): void => {
  const anchors = Array.from(document.querySelectorAll(BANNER_ANCHOR_SELECTOR));
  const anchorBottom = Math.max(0, ...anchors.map(anchor => anchor.getBoundingClientRect().bottom));
  const root = document.querySelector(STICKY_ALERT_ROOT_SELECTOR);
  const rootTop = root?.getBoundingClientRect().top ?? 0;
  document.documentElement.style.setProperty(BANNER_TOP_VAR, `${anchorBottom - rootTop}px`);
};

onMounted(() => {
  if (hasInlineHost) {
    return;
  }
  reposition();
  window.addEventListener('resize', reposition);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', reposition);
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createBlock(_Teleport, {
    to: INLINE_HOST_SELECTOR,
    disabled: !_unref(hasInlineHost)
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["wc-banner-alert wc-banner-alert--full-width", [`wc-banner-alert--${__props.variant}`, { 'wc-banner-alert--inline': _unref(hasInlineHost) }]]),
      role: "alert",
      "aria-live": "assertive",
      "aria-atomic": "true",
      "data-testid": "banner"
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_font_awesome_icon, {
          icon: variantIcon.value,
          class: "alert-icon",
          "aria-hidden": "true",
          "data-testid": "icon"
        }, null, 8, ["icon"]),
        _cache[0] || (_cache[0] = _createTextVNode()),
        _createElementVNode("strong", _hoisted_2, _toDisplayString(__props.message), 1),
        _cache[1] || (_cache[1] = _createTextVNode()),
        _createElementVNode("button", {
          type: "button",
          class: "alert-close",
          "aria-label": _ctx.$t('close'),
          onClick: dismiss,
          "data-testid": "closeButton"
        }, [
          _createVNode(_component_font_awesome_icon, {
            icon: ['fas', 'xmark'],
            "aria-hidden": "true"
          })
        ], 8, _hoisted_3)
      ])
    ], 2)
  ], 8, ["disabled"]))
}
}

})